import { Link } from "react-router-dom";
import { CalendarDays, Clock, PenLine } from "lucide-react";
import { Crumbs } from "../product/ProductHero";
import { QuoteButton } from "../home/CtaButtons";
import { CompareLink } from "../info/InfoHero";
import { ArticleImage } from "./ArticleCard";
import { ArticleBlocks, articleHeadings } from "./ArticleBlocks";
import { catLabel, nlDateLong } from "../../lib/kennisbank";

const Toc = ({ headings, onderwerp }) => (
  <aside className="hidden lg:col-span-3 lg:block" aria-label="In dit artikel">
    <div className="sticky top-28 space-y-6">
      {headings.length > 1 && (
        <nav data-testid="kb-toc">
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500">In dit artikel</p>
          <ul className="mt-3 space-y-2 border-l border-slate-200 text-[14px]">
            {headings.map((h) => <li key={h.id}><a href={`#${h.id}`} className="-ml-px block border-l border-transparent pl-3 leading-snug text-slate-600 hover:border-maas hover:text-maas">{h.text}</a></li>)}
          </ul>
        </nav>
      )}
      <div className="card p-5" data-testid="kb-side-cta">
        <p className="text-[15px] font-semibold text-ink">Advies voor uw woning?</p>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">Ontvang vrijblijvend een persoonlijke prijsindicatie.</p>
        <QuoteButton location="kb-side" onderwerp={onderwerp || undefined} label="Prijsindicatie" className="mt-4 w-full !text-[14px]" />
      </div>
    </div>
  </aside>
);

const Meta = ({ a }) => {
  const updated = a.updated_at && a.published_at && a.updated_at.slice(0, 10) > a.published_at;
  return (
    <ul className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-slate-500" data-testid="kb-article-meta">
      <li className="inline-flex items-center gap-1.5"><PenLine className="h-4 w-4" aria-hidden="true" /> Redactie Maas Kozijn</li>
      {a.published_at && <li className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" aria-hidden="true" /> <time dateTime={a.published_at}>{nlDateLong(a.published_at)}</time></li>}
      {updated && <li>Bijgewerkt op <time dateTime={a.updated_at.slice(0, 10)}>{nlDateLong(a.updated_at)}</time></li>}
      <li className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" aria-hidden="true" /> {a.read_minutes || 2} min lezen</li>
    </ul>
  );
};

// Volledige artikelweergave; gedeeld door de openbare pagina en de voorbeeldweergave in het beheer.
export const ArticleView = ({ a, cats }) => {
  const headings = articleHeadings(a.blocks);
  const label = catLabel(cats, a.category);
  return (
    <article data-testid="kb-article">
      <header className="bg-white">
        <div className="container-tight pb-8 pt-6 sm:pt-8">
          <Crumbs items={[{ label: "Home", href: "/" }, { label: "Kennisbank", href: "/blog" }, { label: a.title || "Artikel", href: `/blog/${a.slug}` }]} />
          <div className="reveal-hero mt-8 max-w-3xl">
            {label && <Link to={`/blog?categorie=${a.category}`} className="eyebrow hover:underline" data-testid="kb-article-category">{label}</Link>}
            <h1 className="h1 mt-3 text-balance lg:text-[2.6rem] xl:text-[2.9rem]" data-testid="kb-article-title">{a.title}</h1>
            <p className="lead mt-4" data-testid="kb-article-excerpt">{a.excerpt}</p>
            <Meta a={a} />
          </div>
        </div>
        {a.image?.path && (
          <div className="container-tight">
            <div className="reveal-hero relative aspect-[16/9] overflow-hidden rounded-3xl bg-slate-100 [animation-delay:120ms] sm:aspect-[21/9]" data-testid="kb-article-hero-image">
              <ArticleImage image={a.image} priority sizes="(min-width: 1280px) 1200px, calc(100vw - 32px)" />
              {a.image.credit && <span className="absolute bottom-3 right-3 rounded-md bg-white/85 px-2 py-0.5 text-[11px] font-medium text-slate-700 backdrop-blur-sm">{a.image.credit}</span>}
            </div>
          </div>
        )}
      </header>
      <div className="container-tight grid gap-10 py-12 sm:py-16 lg:grid-cols-12 lg:gap-12">
        <Toc headings={headings} onderwerp={a.onderwerp} />
        <div className="min-w-0 max-w-[720px] lg:col-span-8 lg:col-start-5">
          <ArticleBlocks blocks={a.blocks} />
          <div className="mt-12 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="kb-article-footer-cta">
            <div>
              <p className="text-[16px] font-semibold text-ink">Vragen over uw eigen situatie?</p>
              <p className="mt-1 text-[14px] text-slate-600">Wij denken vrijblijvend met u mee, ook als u al een offerte heeft.</p>
            </div>
            <CompareLink location="kb-article-footer" label="Offerte vergelijken" />
          </div>
        </div>
      </div>
    </article>
  );
};
